import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ShoppingBag, Plus, Minus, Loader2, ArrowLeft, Repeat } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";
import { CATALOG_ITEMS, getUnitPrice, formatPrice } from "../data/catalog";
import { createCheckoutSession } from "../lib/api";

const Cart = () => {
  const { items, updateQty, removeItem, subtotal, count } = useCart();
  const [loading, setLoading] = useState(false);
  const [params] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (params.get("canceled")) toast.info("Checkout canceled. Your cart is saved.");
  }, [params]);

  const handleCheckout = async () => {
    if (!items.length) return;
    setLoading(true);
    try {
      const { url } = await createCheckoutSession(items);
      window.location.href = url;
    } catch {
      toast.error("Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-8 py-12 md:py-20 min-h-[60vh]">
      <h1 className="font-heading text-3xl md:text-4xl font-bold text-navy-900">Your Cart</h1>

      {items.length === 0 ? (
        <div className="mt-12 flex flex-col items-center text-center gap-5 py-16">
          <span className="w-20 h-20 rounded-full bg-sky-50 flex items-center justify-center">
            <ShoppingBag className="w-8 h-8 text-royal" strokeWidth={1.5} />
          </span>
          <p className="text-slate-500">Your cart is currently empty.</p>
          <Link to="/bundles" className="inline-flex items-center gap-2 bg-navy-900 text-white rounded-full px-7 py-3.5 font-medium hover:bg-navy-800 transition-colors">
            View plans
          </Link>
        </div>
      ) : (
        <div className="mt-10 grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 space-y-4">
            {items.map((item) => {
              const it = CATALOG_ITEMS[item.id];
              const unit = getUnitPrice(item.id, item.mode);
              const key = `${item.id}-${item.mode}`;
              return (
                <div key={key} className="flex gap-5 items-center bg-white rounded-2xl border border-slate-100 p-5" data-testid={`cartpage-item-${key}`}>
                  <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-sky-50 to-white border border-slate-100 flex items-center justify-center shrink-0 p-2.5">
                    <img src="/images/brand/icon_b.png" alt="" className="w-full h-full object-contain" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-navy-900">{it.name}</p>
                    <p className="text-xs text-slate-400">{it.supply}</p>
                    {item.mode === "subscribe" ? (
                      <span className="inline-flex items-center gap-1 mt-1 text-[10px] font-semibold text-royal bg-sky-50 px-2 py-0.5 rounded-full">
                        <Repeat className="w-3 h-3" /> Subscription · {it.frequency}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 mt-1 text-[10px] font-semibold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">One-time</span>
                    )}
                    <div className="flex items-center gap-3 mt-3">
                      <div className="flex items-center border border-slate-200 rounded-full">
                        <button onClick={() => updateQty(item.id, item.mode, item.quantity - 1)} className="p-2"><Minus className="w-3.5 h-3.5" /></button>
                        <span className="w-7 text-center text-sm">{item.quantity}</span>
                        <button onClick={() => updateQty(item.id, item.mode, item.quantity + 1)} className="p-2"><Plus className="w-3.5 h-3.5" /></button>
                      </div>
                      <button onClick={() => removeItem(item.id, item.mode)} className="text-xs text-slate-400 hover:text-red-500">Remove</button>
                    </div>
                  </div>
                  <span className="font-semibold text-navy-900">{formatPrice(unit * item.quantity)}</span>
                </div>
              );
            })}
            <button onClick={() => navigate("/bundles")} className="inline-flex items-center gap-2 text-royal font-medium text-sm hover:gap-3 transition-all mt-2">
              <ArrowLeft className="w-4 h-4" /> Continue shopping
            </button>
          </div>

          <div className="lg:col-span-1">
            <div className="bg-[#FAFBFD] rounded-3xl border border-slate-100 p-7 lg:sticky lg:top-28">
              <h3 className="font-heading text-lg font-semibold text-navy-900">Order Summary</h3>
              <div className="mt-5 space-y-3 text-sm">
                <div className="flex justify-between text-slate-500"><span>Subtotal ({count})</span><span className="text-navy-900 font-medium">{formatPrice(subtotal)}</span></div>
                <div className="flex justify-between text-slate-500"><span>Shipping</span><span className="text-emerald-600 font-medium">{subtotal >= 75 ? "Free" : formatPrice(6.0)}</span></div>
              </div>
              <div className="mt-5 pt-5 border-t border-slate-200 flex justify-between items-center">
                <span className="font-medium text-navy-900">Total</span>
                <span className="font-heading text-2xl font-bold text-navy-900" data-testid="cartpage-total">{formatPrice(subtotal >= 75 ? subtotal : subtotal + 6)}</span>
              </div>
              <button
                onClick={handleCheckout}
                disabled={loading}
                data-testid="cartpage-checkout"
                className="mt-6 w-full bg-royal text-white rounded-full px-8 py-4 font-medium hover:bg-royal-600 transition-colors flex items-center justify-center gap-2 disabled:opacity-70"
              >
                {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> Redirecting…</> : "Proceed to Checkout"}
              </button>
              <p className="mt-3 text-xs text-slate-400 text-center">Secure checkout powered by Stripe</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cart;
